<template>
  <div
    v-editable="blok"
    :class="[
      'flex flex-col gap-4 rounded-lg p-6',
      'bg-shade-200',
      containerLayoutClass,
      ...marginClasses,
      containerClasses,
    ]"
    data-testid="contact-lens-consultant"
  >
    <Headline tag="h4" look="h4" class="gap-4">
      <LazyIconFielmannContactLens class="size-5" />
      {{ $t('contact_lens_consultant_link.snippet_title') }}
    </Headline>
    <p>
      {{ $t('contact_lens_consultant_link.snippet_copy') }}
    </p>
    <div
      :class="isResponsive ? 'sm:ml-auto' : ''"
      data-testid="contact-lens-consultant-button-container"
    >
      <FimButton
        :id="buttonId"
        is-small
        type="primary"
        data-testid="contact-lens-consultant-button"
        :aria-label="$t('contact_lens_consultant_link.snippet_cta')"
        role="button"
        @click="openOverlay"
      >
        {{ $t('contact_lens_consultant_link.snippet_cta') }}
        <template #iconAfter>
          <LazyIconFielmannArrowRight class="size-6" aria-hidden="true" />
        </template>
      </FimButton>

      <QuestionaireOverlay
        v-if="questionnaireProps"
        ref="overlayRef"
        :questionnaire-props="questionnaireProps"
        :data-test-id="
          questionnaireProps.id || `questionaire-${questionnaireProps._uid}`
        "
        @click:close="emit('click:close')"
        @click:open="emit('click:open')"
        @restart="emit('restart')"
        @edit="emit('edit', $event)"
        @show:result="emit('show:result', $event)"
        @select:product="emit('select:product', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  SbContactLensConsultant,
  SbQuestionnaire,
  SbQuestionnaireStep,
} from '../types/storyblok'
import type { QuestionaireResultItem } from '~/constants/questionaire'
import QuestionaireOverlay from '~/storyblok/components/Questionaire/QuestionaireOverlay.vue'

const { blok = {} as SbContactLensConsultant, isResponsive = false } =
  defineProps<{
    blok?: SbContactLensConsultant
    isResponsive?: boolean
  }>()

const { data: questionnaireData } = await useCmsStory({
  params: {
    slug: 'contact-lens-consultant/questionaire',
    baseFolder: CmsBaseFolder.Overlays,
  },
})

const questionnaireProps = questionnaireData?.value?.content
  ?.content?.[0] as SbQuestionnaire

const buttonId = computed(
  () => blok?.id || 'Contact Lens Consultant | Start Contact Lens Consultant',
)

const { marginClasses, containerClasses } = useStoryblokMargins(blok || {})

const containerLayoutClass = computed(() => {
  return isResponsive
    ? 'sm:flex-row sm:items-center sm:px-4 sm:py-2 sm:mt-0 sm:h-14'
    : ''
})

const emit = defineEmits<{
  (e: 'click:close' | 'click:open' | 'restart'): void
  (e: 'edit', params: SbQuestionnaireStep): void
  (e: 'show:result', params: QuestionaireResultItem[]): void
  (e: 'select:product', params: QuestionaireResultItem): void
}>()

const overlayRef = ref<InstanceType<typeof QuestionaireOverlay>>()

const openOverlay = (event: MouseEvent) => {
  overlayRef.value?.open(event)
}
</script>
